import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpUtilService } from "../http-util.service";

@Injectable({
  providedIn: "root",
})
export class CarAllocationService {
  constructor(private http: HttpUtilService) {}

  /**
   * Retrieves the list of active models in sales.
   * Calls the API: ModelSpecificationService.asmx/GetModelActiveInSalesDrpList
   * @returns Observable containing the list of active models.
   */
  getModelActiveInSalesDrpList(): Observable<any> {
    return this.http.get(
      `ModelSpecificationService.asmx/GetModelActiveInSalesDrpList`,
    );
  }

  /**
   * Retrieves all active users from the lookup service.
   * Calls the API: LookUpService.asmx/GetAllActiveUsers
   * @returns Observable containing the list of active users.
   */
  getAllActiveUsers(): Observable<any> {
    return this.http.get(`LookUpService.asmx/GetAllActiveUsers`);
  }

  /**
   * Retrieves the vehicle list based on the selected color, interior color, and model.
   * Calls the API: CarAllocationService.asmx/GetVehicleList
   * @param colour The selected exterior color. Use "All" to retrieve all colors.
   * @param intColour The selected interior color. Use "All" to retrieve all interior colors.
   * @param model The selected model. Use "All" to retrieve all models.
   * @returns Observable containing the list of available vehicles.
   */
  getVehicleList(
    colour: string,
    intColour: string,
    model: string,
  ): Observable<any> {
    const params = `colour=${colour}&intColour=${intColour}&model=${model}`;
    return this.http.get(
      `CarAllocationService.asmx/GetVehicleList?${params}`,
    );
  }

  /**
   * Retrieves the list of available interior colors based on the selected exterior color and model.
   * Calls the API: CarAllocationService.asmx/GetIntColourCode
   * @param colour The selected exterior color. Use "All" to retrieve all colors.
   * @param model The selected model. Use "All" to retrieve all models.
   * @returns Observable containing the list of interior colors.
   */
  getIntColourCode(colour: string, model: string): Observable<any> {
    //const params = `colour=%22${encodeURIComponent(colour)}%22&model=%22${model}%22`;
    const params = `colour=${colour}&model=${model}`;
    return this.http.get(
      `CarAllocationService.asmx/GetIntColourCode?${params}`,
    );
  }

  /**
   * Retrieves the secured COE (Certificate of Entitlement) list.
   * Calls the API: CarAllocationService.asmx/GetSecuredCoeList
   * @returns Observable containing the secured COE list.
   */
  getSecuredCoeList(): Observable<any> {
    return this.http.get(
      `CarAllocationService.asmx/GetSecuredCoeList`,
    );
  }

  getColourList(model: string): Observable<any> {
    return this.http.get(
      `VehicleService.asmx/GetColourList?model=${model}`,
    );
  }

  /**
   * Retrieves vehicle details based on the given case number.
   * Calls the API: CarAllocationService.asmx/GetVehicleDetails
   * @param caseNumber The case number of the vehicle.
   * @returns Observable containing the vehicle details.
   */
  getVehicleDetails(caseNumber: string): Observable<any> {
    const formattedCaseNumber = `%22${caseNumber}%22`; // Agrega las comillas dobles codificadas
    return this.http.get(
      `CarAllocationService.asmx/GetVehicleDetails?caseNumber=${formattedCaseNumber}`,
    );
  }

  /**
   * Retrieves contract details based on the given contract number.
   * Calls the API: CarAllocationService.asmx/GetContractDetails
   * @param contractNo The contract number.
   * @returns Observable containing the contract details.
   */
  getContractDetails(contractNo: string): Observable<any> {
    const payload = { contractNo };
    return this.http.post(
      `CarAllocationService.asmx/GetContractDetails`,
      payload,
    );
  }

  /**
   * Calls the Allocate API to assign a vehicle to a contract.
   * @param payload The allocation details including contract and vehicle data.
   * @returns Observable containing the response.
   */
  allocate(model: any): Observable<any> {
    const payload = { model };
    return this.http.post(
      "CarAllocationService.asmx/Allocate",
      payload,
    );
  }

  /**
   * Calls the DropAllocation API to remove a vehicle allocation from a contract.
   * @param payload The contract number to remove the allocation from.
   * @returns Observable containing the response.
   */
  dropAllocation(contractNo: string): Observable<any> {
    const payload = { contractNo };
    return this.http.post(
      "CarAllocationService.asmx/DropAllocation",
      payload,
    );
  }

  allocateAll(contractNoList: string[]): Observable<any> {
    return this.http.post("CarAllocationService.asmx/AllocateAll", {
      contractNoList,
    });
  }
}
